import React from "react";

interface BulletPoint {
  point: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  bullets?: string | BulletPoint[];
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultBullets = [
  {
    point: "Light Absorption",
    description: "Chlorophyll molecules capture sunlight energy",
    icon: "☀️",
  },
  {
    point: "Water Splitting",
    description: "H2O molecules split into H+ and O2",
    icon: "💧",
  },
  {
    point: "Electron Transport",
    description: "Electrons flow through photosystems",
    icon: "⚡",
  },
  {
    point: "ATP Production",
    description: "Chemical energy is stored as ATP",
    icon: "🔋",
  },
  {
    point: "Carbon Fixation",
    description: "CO2 is converted to glucose",
    icon: "🌱",
  },
  {
    point: "Oxygen Release",
    description: "O2 is released as a byproduct",
    icon: "🌬️",
  },
];

const BulletSlide3 = {
  id: "bullet-slide3",
  name: "Bullet Points (Cards)",
  category: "Bullet Points",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Key Stages of Photosynthesis",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "Understanding the Process Step by Step",
    },
    {
      id: "bullets",
      label: "Bullets (JSON array of { point, description, icon })",
      type: "text",
      defaultValue: JSON.stringify(defaultBullets, null, 2),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #312e81 0%, #4338ca 50%, #6366f1 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "Key Stages of Photosynthesis",
      subtitle = "Understanding the Process Step by Step",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #312e81 0%, #4338ca 50%, #6366f1 100%)",
      bullets = JSON.stringify(defaultBullets),
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleBulletEdit = (index: number, field: string, value: string) => {
      let parsedBullets = defaultBullets;
      try {
        if (typeof bullets === "string") {
          parsedBullets = JSON.parse(bullets);
        }
      } catch (error) {
        console.warn("Error parsing bullets:", error);
      }

      const updatedBullets = [...parsedBullets];
      updatedBullets[index] = {
        ...updatedBullets[index],
        [field]: value,
      };

      const updatedContent = {
        ...content,
        bullets: JSON.stringify(updatedBullets),
      };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    let parsedBullets = defaultBullets;
    try {
      if (typeof bullets === "string") {
        const parsed = JSON.parse(bullets);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedBullets = parsed;
        }
      } else if (Array.isArray(bullets) && bullets.length > 0) {
        parsedBullets = bullets as BulletPoint[];
      }
    } catch (error) {
      console.warn("Error parsing bullets, using defaults:", error);
    }

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 p-8 text-center">
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("title")(e.currentTarget.textContent || "")
            }
            className="text-4xl font-bold mb-2 text-indigo-100 cursor-text"
          >
            {title}
          </div>
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("subtitle")(e.currentTarget.textContent || "")
            }
            className="text-xl font-light text-indigo-100/90 cursor-text"
          >
            {subtitle}
          </div>
        </div>

        {/* Main Content - Three Column Grid */}
        <div className="absolute inset-0 mt-32 mb-16 px-8">
          <div className="h-full grid grid-cols-3 gap-8">
            {parsedBullets.map((bullet, idx) => (
              <div
                key={idx}
                className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-8 
                          border border-indigo-500/20 transform transition-all duration-300 
                          hover:scale-102 hover:bg-white/10"
              >
                {/* Number Badge */}
                <div
                  className="absolute -top-4 -left-4 w-10 h-10 bg-indigo-600/80 
                              rounded-full flex items-center justify-center text-xl 
                              font-bold border border-indigo-400/30 shadow-lg"
                >
                  {idx + 1}
                </div>

                {/* Icon */}
                <div className="text-4xl mb-6 text-center">
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "icon",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="inline-block transform transition-transform duration-300 
                            group-hover:scale-110 cursor-text"
                  >
                    {bullet.icon}
                  </span>
                </div>

                {/* Content */}
                <div className="text-center">
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "point",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="block text-2xl font-bold text-indigo-100 mb-4 cursor-text"
                  >
                    {bullet.point}
                  </span>
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "description",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="block text-base text-indigo-100/80 leading-relaxed max-h-[100px] overflow-y-auto
                              scrollbar-thin scrollbar-track-indigo-900/20 scrollbar-thumb-indigo-600/50"
                  >
                    {bullet.description}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default BulletSlide3;
